import { apiClient } from './apiClient';
import { supabase } from '../lib/supabase';

// Types based on API documentation
export interface AuthResponse {
  user: {
    id: string;
    email: string;
    role: string;
  };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface UserRegistrationData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  heardFrom?: string;
  termsAccepted: boolean;
  /**
   * Account flavor selected at signup. Backend now accepts 'buyer' alongside
   * the existing 'creator' / 'individual' values. Optional — when omitted the
   * backend keeps its historical default.
   */
  accountType?: 'creator' | 'individual' | 'buyer';
}

export interface RegistrationResponse {
  user: {
    id: string;
    email: string;
    role: string;
  };
  session: {
    access_token: string;
    refresh_token: string;
  } | null;
  requiresEmailConfirmation?: boolean;
}

export const authService = {
  /**
   * Login with email and password using Supabase Auth
   */
  login: async (credentials: { email: string; password: string }) => {
    console.log('[auth.login] start');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      console.log('[auth.login] supabase done', { hasSession: !!data?.session, error: error?.message });

      if (error) {
        return {
          success: false,
          error: {
            code: 401,
            message: error.message || 'Invalid email or password',
            details: error,
          },
        };
      }

      if (data.session) {
        localStorage.setItem('accessToken', data.session.access_token);
        localStorage.setItem('refreshToken', data.session.refresh_token);
        console.log('[auth.login] tokens stored, fetching /users/me');

        // Fetch user profile from your API
        const userResponse = await apiClient.get('/users/me');
        console.log('[auth.login] /users/me response', { success: userResponse.success, hasData: !!(userResponse as any).data });
        
        // Check if email is verified (skip for buyer accounts)
        if (userResponse.success && userResponse.data) {
          const user = userResponse.data;
          
          if (user.emailVerified === false && user.accountType !== 'buyer') {
            // Clear tokens for unverified users
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            await supabase.auth.signOut();
            
            return {
              success: false,
              error: {
                code: 403,
                message: 'Please verify your email address before logging in. Check your inbox for the confirmation link.',
                details: { emailVerified: false },
              },
            };
          }
        }
        
        return {
          success: true,
          data: {
            user: userResponse.success ? userResponse.data : data.user,
            accessToken: data.session.access_token,
            refreshToken: data.session.refresh_token,
            expiresIn: data.session.expires_in || 3600,
          },
          message: 'Login successful',
        };
      }

      return {
        success: false,
        error: {
          code: 401,
          message: 'Login failed',
          details: null,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: 500,
          message: error.message || 'An unexpected error occurred',
          details: error,
        },
      };
    }
  },

  /**
   * Register a new user
   * POST /users/register
   */
  register: async (userData: UserRegistrationData) => {
    try {
      const response = await apiClient.post<RegistrationResponse>('/users/register', userData);
      
      if (!response.success) {
        return response;
      }

      // Check if email confirmation is required
      if (response.data?.requiresEmailConfirmation) {
        return {
          success: true,
          data: {
            user: response.data.user,
            session: null,
            requiresEmailConfirmation: true,
          },
          message: 'Account created! Please check your email to confirm your account.',
        };
      }

      // User is auto-confirmed, store tokens and fetch profile
      if (response.data?.session) {
        const { access_token, refresh_token } = response.data.session;
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        
        const userResponse = await apiClient.get('/users/me');
        
        return {
          success: true,
          data: {
            user: userResponse.success ? userResponse.data : response.data.user,
            session: response.data.session,
          },
          message: 'Registration successful',
        };
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: 500,
          message: error.message || 'Registration failed',
          details: error,
        },
      };
    }
  },

  /**
   * Refresh access token
   * POST /auth/refresh
   */
  refreshToken: async (refreshToken: string) => {
    const response = await apiClient.post<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>('/auth/refresh', { refreshToken });
    
    if (response.success) {
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    
    return response;
  },

  /**
   * Logout user using Supabase Auth
   */
  logout: async () => {
    try {
      await supabase.auth.signOut();
      
      // Clear tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      return {
        success: true,
        data: null,
        message: 'Logged out successfully',
      };
    } catch (error: any) {
      // Clear tokens even if logout fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      return {
        success: false,
        error: {
          code: 500,
          message: error.message || 'Logout failed',
          details: error,
        },
      };
    }
  },

  /**
   * Request password reset
   * POST /auth/reset-password
   */
  forgotPassword: async (email: string) => {
    return await apiClient.post('/auth/reset-password', { email });
  },

  /**
   * Reset password with token
   * POST /auth/reset-password
   */
  resetPassword: async (email: string) => {
    return await apiClient.post('/auth/reset-password', { email });
  },

  /**
   * Change password for authenticated user
   * POST /auth/change-password
   */
  changePassword: async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    return await apiClient.post('/auth/change-password', data);
  },
  
  /**
   * Get current user profile
   * GET /users/me
   */
  getCurrentUser: async () => {
    return await apiClient.get('/users/me');
  },
  
  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('accessToken');
    return !!token;
  },
  
  /**
   * Get stored access token
   */
  getAccessToken: () => {
    return localStorage.getItem('accessToken');
  },

  /**
   * Set a permanent password for a temp-password buyer account.
   * POST /auth/set-password
   */
  setPermanentPassword: async (newPassword: string) => {
    const response = await apiClient.post('/auth/set-password', { newPassword });
    if (response.success) {
      sessionStorage.removeItem('promptPermanentPassword');
      // Refetch user profile to update tempPassword flag
      await apiClient.get('/users/me');
    }
    return response;
  },
}; 